export class StringUtils {
  /**
   * Trims the string, transforms it to lower case, and removes combining diacritical marks from it.
   * https://en.wikipedia.org/wiki/Combining_Diacritical_Marks
   *
   * normalize() to NFD Unicode normal form decomposes combined graphemes into the combination of
   * simple ones.
   * ie: The è of Crème ends up expressed as e + ̀.
   *
   * replace() uses a regex to match the U+0300 → U+036F char’s range in the Unicode standard that
   * refers to Combining Diacritical Marks
   * http://unicode.org/charts///PDF/Unicode-3.2/U32-0300.pdf
   *
   * @param string - The string to normalize.
   * @returns The normalized version of the string.
   */
  static normalize(string: string): string {
    return string
      .normalize('NFD')
      .replace(/[\u0300-\u036F]/g, '')
      .trim()
      .toLowerCase()
  }

  /**
   * Given a string and desired size, returns an abbreviation.
   * - For more than one word, only the initials are returned.
   * - For a single word, the first characters are returned.
   *
   * @param string - The string we want to get the abbreviation from
   * @param maxLength - Abbreviation length
   * @param [addDot] - Wether a dot is needed at the end of the abbreviation or not
   *                   (only applies for non-acronym abbreviations)
   * @returns string - String abbreviation
   */
  static getAbbreviation(string: string, maxLength: number, addDot = false): string {
    const words = string.split(' ')
    const isAcronym = words.length > 1

    if (isAcronym) {
      return words
        .map(word => word.charAt(0))
        .join('')
        .slice(0, maxLength)
        .toUpperCase()
    }

    const abbr = string.slice(0, maxLength)
    const mustAddDot = addDot && abbr.length < string.length
    return `${abbr}${mustAddDot ? '.' : ''}`
  }

  /**
   * Checks whether string is a linkable URL.
   *
   * Have in mind that this is based on a very simple regexp and it doesn't
   * pretend to be an exhaustive verifier. This means that wrong URLs may
   * still return `true` by this method, just because they start with an
   * HTTP/S protocol.
   *
   * @param string - The string to check.
   * @returns Whether string is a linkable URL or not.
   */
  static isLink(string: string): boolean {
    return /^(http(s?)):\/\//i.test(string)
  }

  /**
   * Converts the string into a boolean value
   * @param string - The string to cast
   * @returns true/false based on the specified param
   */
  static toBoolean(string: string): boolean {
    return string?.toLowerCase() === 'true'
  }

  static copyToClipboard(string: string): Promise<void> {
    return navigator.clipboard.writeText(string)
  }

  static split(values: string, separator = ';\n'): Array<string> {
    const separatorsRegex = new RegExp(`[${separator}]`)
    return values
      .split(separatorsRegex)
      .map(value => value.trim())
      .filter(value => value?.length)
  }

  static compareCaseInsensitive(string1: string, string2: string): number {
    if (string1.toUpperCase() < string2.toUpperCase()) {
      return -1
    }
    if (string1.toUpperCase() > string2.toUpperCase()) {
      return 1
    }
    return 0
  }

  static sanitizeText(text: string): any {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = text
    return tempDiv.textContent
  }

  static removeSpacesFromString(string: string): string {
    return string.replace(/\s+/g, '')
  }
}
